import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Alert,
    CircularProgress,
    AppBar,
    Toolbar,
    Menu,
    MenuItem as MenuItemComponent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    FormControlLabel,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon,
    Home as HomeIcon,
    Menu as MenuIcon,
    DragIndicator as DragIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    getServices,
    createService,
    updateService,
    deleteService,
    updateServiceSortOrder,
    getServiceSettings,
    updateServiceSettings,
} from "../../../api/api";
import { getCurrentBranchId } from "../../../utils/auth";
import Logo from "../../../assests/logo.png";

// Sortable Table Row Component
const SortableServiceRow = ({ service, onEdit, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: service.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
    };

    return (
        <TableRow ref={setNodeRef} style={style}>
            <TableCell sx={{ width: 50, px: 1 }}>
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{
                        cursor: 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        '&:hover': {
                            color: 'primary.main',
                        },
                    }}
                >
                    <DragIcon />
                </Box>
            </TableCell>
            <TableCell>{service.service_name}</TableCell>
            <TableCell>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: "monospace",
                        fontWeight: "bold",
                        color: "primary.main",
                    }}
                >
                    {service.service_tag}
                </Typography>
            </TableCell>
            <TableCell align="right">
                <IconButton
                    onClick={() => onEdit(service)}
                    color="primary"
                    size="small"
                >
                    <EditIcon />
                </IconButton>
                <IconButton
                    onClick={() => onDelete(service.id)}
                    color="error"
                    size="small"
                >
                    <DeleteIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

const ServiceManagement = () => {
    const navigate = useNavigate();
    const branchId = getCurrentBranchId();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [formData, setFormData] = useState({
        service_name: "",
        service_tag: "",
    });
    const [serviceSelectionEnabled, setServiceSelectionEnabled] = useState(true);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        fetchServices();
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await getServiceSettings(branchId);
            if (response.success) {
                setServiceSelectionEnabled(response.data.service_selection_enabled);
            }
        } catch (err) {
            console.error("Error fetching settings:", err);
        }
    };

    // Fix scrolling issue
    useEffect(() => {
        const rootElement = document.getElementById('root');
        const htmlElement = document.documentElement;
        const bodyElement = document.body;

        // Save original styles
        const originalBodyOverflow = bodyElement.style.overflow;
        const originalBodyHeight = bodyElement.style.height;
        const originalHtmlOverflow = htmlElement.style.overflow;
        const originalHtmlHeight = htmlElement.style.height;
        const originalRootOverflow = rootElement?.style.overflow;
        const originalRootHeight = rootElement?.style.height;

        // Enable scrolling
        bodyElement.style.overflow = 'auto';
        bodyElement.style.height = 'auto';
        htmlElement.style.overflow = 'auto';
        htmlElement.style.height = 'auto';
        if (rootElement) {
            rootElement.style.overflow = 'auto';
            rootElement.style.height = 'auto';
        }

        // Add CSS class
        bodyElement.classList.add('scrollable-page');
        htmlElement.classList.add('scrollable-page');

        return () => {
            // Restore original styles
            bodyElement.style.overflow = originalBodyOverflow;
            bodyElement.style.height = originalBodyHeight;
            htmlElement.style.overflow = originalHtmlOverflow;
            htmlElement.style.height = originalHtmlHeight;
            if (rootElement) {
                rootElement.style.overflow = originalRootOverflow;
                rootElement.style.height = originalRootHeight;
            }
            bodyElement.classList.remove('scrollable-page');
            htmlElement.classList.remove('scrollable-page');
        };
    }, []);

    const fetchServices = async () => {
        try {
            setLoading(true);
            const response = await getServices(branchId);
            if (response.success) {
                setServices(response.data);
            } else {
                setError("Failed to fetch services");
            }
        } catch (err) {
            setError("Error loading services");
            console.error("Error fetching services:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateService = () => {
        setEditingService(null);
        setFormData({ service_name: "", service_tag: "" });
        setOpenDialog(true);
    };

    const handleEditService = (service) => {
        setEditingService(service);
        setFormData({
            service_name: service.service_name,
            service_tag: service.service_tag,
        });
        setOpenDialog(true);
    };

    const handleDeleteService = async (id) => {
        if (window.confirm("Are you sure you want to delete this service?")) {
            try {
                const response = await deleteService(id);
                if (response.success) {
                    setServices(services.filter((service) => service.id !== id));
                    setSuccess("Service deleted successfully");
                    setTimeout(() => setSuccess(null), 3000);
                } else {
                    setError("Failed to delete service");
                }
            } catch (err) {
                setError("Error deleting service");
                console.error("Error deleting service:", err);
            }
        }
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setEditingService(null);
        setFormData({ service_name: "", service_tag: "" });
    };

    const handleSave = async () => {
        try {
            setError(null);
            const data = {
                ...formData,
                branch_id: branchId,
            };

            console.log('Saving service:', data);

            let response;
            if (editingService) {
                response = await updateService(editingService.id, formData);
            } else {
                response = await createService(data);
            }

            console.log('Service save response:', response);

            if (response.success) {
                await fetchServices();
                setSuccess(
                    editingService
                        ? "Service updated successfully"
                        : "Service created successfully"
                );
                setTimeout(() => setSuccess(null), 3000);
                handleDialogClose();
            } else {
                const errorMsg = response.error || response.errors || "Failed to save service";
                setError(errorMsg);
            }
        } catch (err) {
            console.error("Error saving service:", err);
            const errorMsg = err.error || err.response?.data?.error || err.message || "Error saving service";
            setError(errorMsg);
        }
    };

    const handleToggleServiceSelection = async (event) => {
        const newValue = event.target.checked;
        setServiceSelectionEnabled(newValue);
        try {
            await updateServiceSettings(branchId, { service_selection_enabled: newValue });
            setSuccess("Settings updated successfully");
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error("Error updating settings:", err);
            setError("Failed to update settings");
            setServiceSelectionEnabled(!newValue); // Revert
        }
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleNavigateHome = () => {
        navigate("/home");
        handleMenuClose();
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = services.findIndex((service) => service.id === active.id);
        const newIndex = services.findIndex((service) => service.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        const newItems = arrayMove(services, oldIndex, newIndex);
        setServices(newItems);

        // Update sort order in backend
        try {
            const servicesWithNewOrder = newItems.map((service, index) => ({
                id: parseInt(service.id, 10),
                service_sort_order: parseInt(index, 10),
            }));

            console.log('Updating sort order:', servicesWithNewOrder);
            const response = await updateServiceSortOrder(servicesWithNewOrder);

            if (response.success) {
                setSuccess("Service order updated successfully");
                setTimeout(() => setSuccess(null), 3000);
            } else {
                const errorMsg = response.error || response.errors || "Failed to update service order";
                setError(errorMsg);
                // Revert the change
                setServices(services);
            }
        } catch (err) {
            console.error("Error updating sort order:", err);
            const errorMsg = err.error || err.response?.data?.error || err.message || "Failed to update service order";
            setError(errorMsg);
            // Revert the change
            setServices(services);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto'
        }}>
            {/* Navbar */}
            <AppBar position="static">
                <Toolbar>
                    <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                        <img
                            src={Logo}
                            alt="Logo"
                            style={{
                                height: "32px",
                                marginRight: "16px",
                                filter: "brightness(0) invert(1)",
                            }}
                        />
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="back"
                            onClick={handleNavigateHome}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Box>

                    <Typography
                        variant="h6"
                        component="div"
                        sx={{ flexGrow: 1 }}
                    >
                        Service Management
                    </Typography>

                    <Button
                        color="inherit"
                        startIcon={<HomeIcon />}
                        onClick={handleNavigateHome}
                        sx={{ mr: 1 }}
                    >
                        Home
                    </Button>

                    <IconButton
                        size="large"
                        edge="end"
                        color="inherit"
                        aria-label="menu"
                        onClick={handleMenuOpen}
                    >
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Navigation Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 200,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    },
                }}
            >
                <MenuItemComponent onClick={handleNavigateHome}>
                    <HomeIcon sx={{ mr: 1 }} />
                    Admin Home
                </MenuItemComponent>
            </Menu>

            <Box sx={{
                p: 3,
                flex: 1,
                overflow: 'auto'
            }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Typography variant="h4" component="h1">
                        Service Management
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateService}
                    >
                        Add New Service
                    </Button>
                </Box>

                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={serviceSelectionEnabled}
                                onChange={handleToggleServiceSelection}
                                color="primary"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Enable Service Selection Page
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    If disabled, users will skip the service selection page and be assigned a queue number directly.
                                </Typography>
                            </Box>
                        }
                    />
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3 }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert
                        severity="success"
                        sx={{ mb: 3 }}
                        onClose={() => setSuccess(null)}
                    >
                        {success}
                    </Alert>
                )}

                <TableContainer component={Paper}>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 50 }}></TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Service Name</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Service Tag</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <SortableContext
                                    items={services.map((service) => service.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {services.map((service) => (
                                        <SortableServiceRow
                                            key={service.id}
                                            service={service}
                                            onEdit={handleEditService}
                                            onDelete={handleDeleteService}
                                        />
                                    ))}
                                </SortableContext>
                            </TableBody>
                        </Table>
                    </DndContext>
                </TableContainer>

                {services.length === 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                            No services found. Create your first service to get started!
                        </Typography>
                    </Box>
                )}

                {/* Add/Edit Dialog */}
                <Dialog
                    open={openDialog}
                    onClose={handleDialogClose}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {editingService ? "Edit Service" : "Create New Service"}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                label="Service Name"
                                value={formData.service_name}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        service_name: e.target.value,
                                    })
                                }
                                margin="normal"
                                required
                            />
                            <TextField
                                fullWidth
                                label="Service Tag"
                                value={formData.service_tag}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        service_tag: e.target.value.toUpperCase(),
                                    })
                                }
                                margin="normal"
                                required
                                helperText="Tag will be displayed on queue cards (e.g., FARGE, MAL, TAPET)"
                                inputProps={{
                                    maxLength: 50,
                                    style: { textTransform: "uppercase" },
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose}>Cancel</Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={!formData.service_name || !formData.service_tag}
                        >
                            {editingService ? "Update" : "Create"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};

export default ServiceManagement;

